import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FileText, ArrowLeft, User, Calendar, Edit, Trash2, Plus, RotateCcw, Shield } from 'lucide-react';
import { Card } from '../components/ui/card';
import Navigation from '../components/Navigation';
import { getAdminHeaders } from '../lib/auth';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const actionIcons = {
  criar: <Plus className="w-4 h-4 text-green-600" />,
  editar: <Edit className="w-4 h-4 text-blue-600" />,
  excluir: <Trash2 className="w-4 h-4 text-red-600" />,
  restaurar: <RotateCcw className="w-4 h-4 text-amber-600" />,
  backup: <Shield className="w-4 h-4 text-[#1A4D2E]" />,
};

const actionColors = {
  criar: 'bg-green-50 border-green-200',
  editar: 'bg-blue-50 border-blue-200',
  excluir: 'bg-red-50 border-red-200',
  restaurar: 'bg-amber-50 border-amber-200',
  backup: 'bg-[#F2F7F4] border-[#E1ECE5]',
};

const entityLabels = {
  crianca: 'Crianca',
  atividade_diaria: 'Ativ. Diaria',
  atividade_semanal: 'Ativ. Semanal',
  usuario: 'Usuario',
};

const LogsEdicao = () => {
  const navigate = useNavigate();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [restoringId, setRestoringId] = useState(null);

  useEffect(() => {
    fetchLogs();
  }, []);

  const fetchLogs = async () => {
    try {
      const res = await axios.get(`${API}/admin/logs?limit=100`, { headers: getAdminHeaders() });
      setLogs(res.data.logs || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleRestore = async (log) => {
    setRestoringId(log.entity_id);
    try {
      const res = await axios.post(
        `${API}/admin/snapshots/${log.entity_type}/${log.entity_id}/restore`,
        {},
        { headers: getAdminHeaders() }
      );
      toast.success(res.data.message);
      fetchLogs();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Nenhum snapshot disponivel para restauracao');
    } finally {
      setRestoringId(null);
    }
  };

  const canRestore = (log) => ['editar', 'excluir'].includes(log.action) && log.entity_id && log.entity_id !== '-';

  const formatDate = (ts) => {
    try {
      return new Date(ts).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });
    } catch { return ts; }
  };

  return (
    <div className="min-h-screen bg-[#F9F9F7] pb-24">
      <div className="bg-[#1A4D2E] text-white px-6 py-8">
        <div className="max-w-5xl mx-auto">
          <button onClick={() => navigate(-1)} className="hover:bg-white/10 rounded-full p-2 transition-all mb-2">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-3">
            <FileText className="w-7 h-7" />
            <h1 className="font-nunito font-bold text-3xl">Logs de Edicao</h1>
          </div>
          <p className="font-inter text-white/70 text-sm mt-1">Histórico de todas as alterações</p>
          <button
            data-testid="go-to-backups-btn"
            onClick={() => navigate('/backups')}
            className="mt-3 flex items-center gap-2 bg-white/15 hover:bg-white/25 text-white px-4 py-2 rounded-full text-sm font-inter font-semibold transition-all"
          >
            <Shield className="w-4 h-4" /> Backup e Restauracao
          </button>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-6">
        {loading ? (
          <p className="text-gray-500 text-center py-8">Carregando...</p>
        ) : logs.length === 0 ? (
          <Card className="bg-white rounded-2xl p-12 text-center shadow-md border border-[#E1ECE5]">
            <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="font-inter text-gray-500">Nenhum log registrado ainda</p>
          </Card>
        ) : (
          <div className="space-y-3">
            {logs.map((log, idx) => (
              <Card key={idx} className={`rounded-xl p-4 shadow-sm border ${actionColors[log.action] || 'bg-white border-gray-200'}`}>
                  <div className="flex items-start gap-3">
                  <div className="mt-1">
                    {actionIcons[log.action] || <Edit className="w-4 h-4 text-gray-500" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-nunito font-bold text-[#1A4D2E] capitalize">{log.action}</span>
                      <span className="bg-white/80 px-2 py-0.5 rounded text-xs font-semibold text-gray-600">
                        {entityLabels[log.entity_type] || log.entity_type}
                      </span>
                    </div>
                    {log.details && (
                      <p className="font-inter text-sm text-gray-700 mt-1">{log.details}</p>
                    )}
                    <div className="flex items-center gap-3 mt-2 text-xs text-gray-500">
                      <span className="flex items-center gap-1">
                        <User className="w-3 h-3" /> {log.admin_name}
                      </span>
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" /> {formatDate(log.timestamp)}
                      </span>
                    </div>
                  </div>
                  {canRestore(log) && (
                    <button
                      data-testid={`restore-log-${idx}`}
                      onClick={() => handleRestore(log)}
                      disabled={restoringId === log.entity_id}
                      className="shrink-0 flex items-center gap-1 bg-white text-[#1A4D2E] border border-[#1A4D2E]/20 px-2.5 py-1 rounded-full text-xs font-inter font-semibold hover:bg-[#F2F7F4] transition-all disabled:opacity-50"
                      title="Restaurar dados anteriores"
                    >
                      <RotateCcw className={`w-3 h-3 ${restoringId === log.entity_id ? 'animate-spin' : ''}`} />
                      {restoringId === log.entity_id ? '...' : 'Restaurar'}
                    </button>
                  )}
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
      <Navigation active="inicio" />
    </div>
  );
};

export default LogsEdicao;
